import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CallsApi } from '../../api/resources';
import { errorMessage } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { useCan } from '../../components/Can';
import ConfirmModal from '../../components/ConfirmModal';
import Icon from '../../components/Icon';
import RecordingPlayer from './RecordingPlayer';
import { TYPE_CLASS, TYPE_LABEL, fmtDuration } from './callUtils';
import { fmtDate } from './useCrmLookups';

const TYPES = ['incoming', 'outgoing', 'missed', 'rejected'];

/** Local-day bounds as ISO, so "today" means the viewer's today. */
function dayRange(from, to) {
  const out = {};
  if (from) out.from = new Date(`${from}T00:00:00`).toISOString();
  if (to) {
    const end = new Date(`${to}T00:00:00`);
    end.setDate(end.getDate() + 1);
    out.to = end.toISOString();
  }
  return out;
}

export default function CallsPage() {
  const { can } = useAuth();
  const canDelete = useCan('crm.calls.delete');
  const seeAll = can('crm.calls.view_all');

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [onlyRec, setOnlyRec] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const params = {
      search: search || undefined,
      call_type: type || undefined,
      has_recording: onlyRec ? 1 : undefined,
      ...dayRange(from, to),
    };
    try {
      const [list, sum] = await Promise.all([CallsApi.list({ ...params, page }), CallsApi.summary(params)]);
      setRows(list.data);
      setMeta(list.meta);
      setSummary(sum);
    } catch (err) {
      setError(errorMessage(err, 'Could not load calls.'));
    } finally {
      setLoading(false);
    }
  }, [page, search, type, from, to, onlyRec]);

  useEffect(() => {
    load();
  }, [load]);

  const filter = (setter) => (e) => {
    setPage(1);
    setter(e.target.type === 'checkbox' ? e.target.checked : e.target.value);
  };

  const doDelete = async () => {
    try {
      await CallsApi.remove(deleting.id);
      setDeleting(null);
      load();
    } catch (err) {
      setDeleting(null);
      setError(errorMessage(err, 'Could not delete the call.'));
    }
  };

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">Calls</h1>
          <p className="page-sub">
            {seeAll ? 'Calls uploaded by the Android app from all staff.' : 'Your calls from the Android app.'}
          </p>
        </div>
      </div>

      {error && <div className="notice notice-error">{error}</div>}

      {summary && (
        <div className="stat-grid" style={{ marginBottom: 16 }}>
          {[
            ['Total', summary.total],
            ['Outgoing', summary.outgoing],
            ['Incoming', summary.incoming],
            ['Missed', summary.missed],
            ['Talk time', fmtDuration(summary.duration)],
          ].map(([label, value]) => (
            <div className="stat" key={label}>
              <div>
                <div className="stat-label">{label}</div>
                <div className="stat-value">{value}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <div className="toolbar">
            <div className="search">
              <Icon name="search" />
              <input type="search" placeholder="Search name or number…" value={search} onChange={filter(setSearch)} />
            </div>
            <select className="select-sm" value={type} onChange={filter(setType)}>
              <option value="">All types</option>
              {TYPES.map((t) => (
                <option key={t} value={t}>{TYPE_LABEL[t]}</option>
              ))}
            </select>
            <input className="select-sm" type="date" value={from} onChange={filter(setFrom)} title="From" />
            <input className="select-sm" type="date" value={to} onChange={filter(setTo)} title="To" />
            <label className="check">
              <input type="checkbox" checked={onlyRec} onChange={filter(setOnlyRec)} />
              With recording
            </label>
          </div>
          {meta && <span className="muted">{meta.total} total</span>}
        </div>

        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>When</th>
                <th>Number</th>
                <th>Type</th>
                <th>Duration</th>
                {seeAll && <th>Staff</th>}
                <th>Recording</th>
                {canDelete && <th style={{ textAlign: 'right' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading &&
                [0, 1, 2, 3].map((i) => (
                  <tr key={i}>
                    {Array.from({ length: 5 + (seeAll ? 1 : 0) + (canDelete ? 1 : 0) }, (_, j) => (
                      <td key={j}><span className="skeleton" style={{ width: 90 }} /></td>
                    ))}
                  </tr>
                ))}
              {!loading &&
                rows.map((c) => (
                  <tr key={c.id}>
                    <td>{fmtDate(c.call_timestamp)}</td>
                    <td>
                      <div className="name">{c.lead ? <Link to={`/crm/leads/${c.lead.id}`}>{c.lead.name}</Link> : c.caller_name || '—'}</div>
                      <div className="sub">
                        {c.caller_number}
                        {c.lead && <span className="badge badge-plain" style={{ marginLeft: 6 }}>Lead</span>}
                        {c.followup_id && <span className="badge badge-ok" style={{ marginLeft: 6 }}>Follow-up logged</span>}
                      </div>
                    </td>
                    <td><span className={TYPE_CLASS[c.call_type]}>{TYPE_LABEL[c.call_type]}</span></td>
                    <td>{fmtDuration(c.call_duration)}</td>
                    {seeAll && <td>{c.staff?.name || '—'}</td>}
                    <td>
                      {c.recordings.length
                        ? c.recordings.map((r) => <RecordingPlayer key={r.id} recording={r} />)
                        : <span className="muted">—</span>}
                    </td>
                    {canDelete && (
                      <td>
                        <div className="row-actions">
                          <button className="icon-btn danger" title="Delete" onClick={() => setDeleting(c)}>
                            <Icon name="trash" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {!loading && rows.length === 0 && (
          <div className="empty">
            <div className="empty-icon"><Icon name="phone" size={26} /></div>
            <strong>No calls found</strong>
            Calls appear here once staff sign in to the Android app.
          </div>
        )}

        {meta && meta.last_page > 1 && (
          <div className="pager">
            <span className="muted">Page {meta.current_page} of {meta.last_page}</span>
            <div className="toolbar">
              <button className="btn btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
              <button className="btn btn-sm" disabled={page >= meta.last_page} onClick={() => setPage((p) => p + 1)}>Next</button>
            </div>
          </div>
        )}
      </div>

      {deleting && (
        <ConfirmModal
          title="Delete call"
          message={`Delete this call with ${deleting.caller_number} and its recording? This cannot be undone.`}
          onConfirm={doDelete}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  );
}
